/**
 * ApplicationDetailPage
 * Shows full application detail with user info
 * Route: /credit/applications/:applicationId?directoryId=xxx
 */

import { useParams, useSearchParams, Link } from 'react-router-dom';
import { RefreshCw, AlertTriangle, ExternalLink } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { useApplicationDetail } from '@/features/credit/hooks';
import { FeeBreakdown } from '@/features/credit/components/FeeBreakdown';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES } from '@/constants';
import { HelpButton } from '@/features/help';

// ─── Helpers ──────────────────────────────────────────────────────────────────

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info';

function getStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'SIGNED':
    case 'DISBURSED':
      return 'success';
    case 'PENDING_SIGNATURE':
    case 'CREATED':
      return 'warning';
    case 'EXPIRED':
    case 'CANCELLED':
      return 'danger';
    default:
      return 'default';
  }
}

function formatDateShort(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function InfoRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium tabular-nums text-foreground">{value ?? '-'}</span>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={`card-skeleton-${i.toString()}`} className="rounded-lg border border-border bg-card p-5">
            <Skeleton variant="text" width="50%" height={16} />
            <div className="mt-4 space-y-3">
              {Array.from({ length: 5 }).map((_, j) => (
                <Skeleton key={`row-${j.toString()}`} variant="text" height={14} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Error State ──────────────────────────────────────────────────────────────

function DetailError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-card px-6 py-16">
      <AlertTriangle size={48} className="mb-4 text-muted-foreground" />
      <h3 className="text-lg font-semibold text-foreground">
        Error al cargar la solicitud
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Hubo un error al obtener los datos. Intenta de nuevo.
      </p>
      <Button className="mt-4" onClick={onRetry}>
        <RefreshCw size={16} />
        Reintentar
      </Button>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function ApplicationDetailPage() {
  const { applicationId = '' } = useParams<{ applicationId: string }>();
  const [searchParams] = useSearchParams();
  const directoryId = searchParams.get('directoryId') ?? '';

  const { application, user, isLoading, isError, refetch } = useApplicationDetail(
    applicationId,
    directoryId,
  );

  const breadcrumbs = [
    { label: 'Inicio', path: ROUTES.DASHBOARD },
    { label: 'Originacion', path: ROUTES.PIPELINE_MATRIX },
    { label: 'Solicitudes', path: ROUTES.APPLICATIONS_LIST },
    { label: `Solicitud #${applicationId.slice(0, 8)}`, path: '#' },
  ];

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title={`Detalle de Solicitud #${applicationId.slice(0, 8)}`}
          breadcrumbs={breadcrumbs}
        />
        <DetailSkeleton />
      </div>
    );
  }

  if (isError || !application) {
    return (
      <div>
        <PageHeader
          title={`Detalle de Solicitud #${applicationId.slice(0, 8)}`}
          breadcrumbs={breadcrumbs}
        />
        <DetailError onRetry={() => refetch()} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`Detalle de Solicitud #${application.application_id.slice(0, 8)}`}
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex items-center gap-2">
            <HelpButton pageKey="application-detail" />
            <Link to={`/credit/profiles/${directoryId}`}>
              <Button variant="outline">Ver Perfil Financiero</Button>
            </Link>
            <Button onClick={() => refetch()}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* Card: Usuario */}
          <Card title="Usuario">
            <div className="space-y-1">
              {user && (
                <>
                  <InfoRow label="Telefono" value={user.phone_number} />
                  <InfoRow label="Directory ID" value={user.directory_id} />
                  <InfoRow label="People ID" value={user.people_id} />
                  <div className="mt-3">
                    <Link
                      to={`/credit/profiles/${user.directory_id}`}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      Ver Perfil Financiero
                    </Link>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Card: Solicitud */}
          <Card title="Solicitud">
            <div className="space-y-1">
              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-muted-foreground">Estado</span>
                <Badge variant={getStatusVariant(application.status)}>
                  {application.status}
                </Badge>
              </div>
              <FeeBreakdown
                requestedAmountMxn={application.amount_mxn}
                openingFeeMxn={application.opening_fee_mxn}
                openingFeeIvaMxn={application.opening_fee_iva_mxn}
                openingFeePct={application.opening_fee_pct}
                financedAmountMxn={application.financed_amount_mxn}
              />
              <InfoRow label="Plazo" value={`${application.term_weeks} semanas`} />
              <InfoRow label="Tasa Anual" value={`${(application.annual_rate * 100).toFixed(1)}%`} />
              <InfoRow label="Pago Semanal" value={formatCurrency(application.weekly_payment_mxn, 'MXN', 'es-MX')} />

              <div className="mt-2 border-t border-border pt-2">
                <div className="flex justify-between py-1.5">
                  <span className="text-sm text-muted-foreground">Oferta</span>
                  <Link
                    to={`/credit/offers/${application.offer_id}?directoryId=${directoryId}`}
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {application.offer_id.slice(0, 8)}...
                  </Link>
                </div>

                {application.contract_view_url && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-sm text-muted-foreground">Contrato</span>
                    <a
                      href={application.contract_view_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                    >
                      Ver contrato
                      <ExternalLink size={12} />
                    </a>
                  </div>
                )}
              </div>

              <div className="mt-2 border-t border-border pt-2">
                <InfoRow label="Expira" value={formatDateShort(application.expires_at)} />
                <InfoRow label="Firmada" value={formatDateShort(application.signed_at)} />
                <InfoRow label="Creada" value={formatDateShort(application.created_at)} />
                <InfoRow label="Actualizada" value={formatDateShort(application.updated_at)} />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
